import { Action, createReducer, on } from '@ngrx/store';
import { LoadStatus } from '../../load-status';
import {
    dateManipulationCheckFailed,
    dateManipulationCheckLoaded,
    dateManipulationCheckTimestampsRestoredFromStorage,
    externalTimestampReceived,
    initDateManipulationCheck,
    initialTimestampCheckRequested,
    timestampCheckRequested,
} from '../actions/date-manipulation-check.actions';
import {
    DateManipulationCheckState,
    initialDateManipulationCheckState,
    PersistedTimestamps,
    TIMESTAMP_CHECK_TOLERANCE_MS,
} from '../date-manipulation-check-collection.state';

/**
 * Marks the slice initialized and loading. `initialized` flips here, at the **start** of the
 * init chain — before the `IsaliveSet` fetch — mirroring the legacy service setting
 * `isInitialized = true` at the top of `init()`, ahead of any network call.
 *
 * @param state the date-manipulation-check slice
 */
export function dateManipulationCheckInitReducer(
    state: Readonly<DateManipulationCheckState>,
): Readonly<DateManipulationCheckState> {
    return { ...state, initialized: true, loadStatus: LoadStatus.Loading };
}

/**
 * Restores the persisted timestamps, absorbing the legacy `init()`'s `stored ?? now` /
 * `stored ?? invalid` defaulting. The reducer owns the `Date.now()` read so the action itself
 * carries only what storage held — an internal timestamp defaults to *now* when nothing was
 * stored (so a fresh device starts from a baseline that trivially passes its own first check);
 * an external timestamp has no such default, since it is only ever set from the gateway response
 * and stays `''` (invalid) until that arrives.
 *
 * @param state the date-manipulation-check slice
 * @param props the action payload
 * @param props.timestamps the persisted timestamps, or both `''` when nothing was stored
 * @returns the slice with the timestamps restored
 */
export function dateManipulationCheckRestoreTimestampsReducer(
    state: Readonly<DateManipulationCheckState>,
    props: { timestamps: PersistedTimestamps },
): Readonly<DateManipulationCheckState> {
    return {
        ...state,
        lastValidInternalTimestamp: props.timestamps.lastValidInternalTimestamp || new Date().toISOString(),
        lastValidExternalTimestamp: props.timestamps.lastValidExternalTimestamp,
    };
}

/**
 * Records a timestamp received from the trusted gateway as the new external baseline.
 *
 * @param state the date-manipulation-check slice
 * @param props the action payload
 * @param props.timestamp the external timestamp to record
 * @returns the slice with the external timestamp replaced
 */
export function dateManipulationCheckSetExternalTimestampReducer(
    state: Readonly<DateManipulationCheckState>,
    props: { timestamp: string },
): Readonly<DateManipulationCheckState> {
    return { ...state, lastValidExternalTimestamp: props.timestamp };
}

/**
 * The whole validity policy, ported exactly from the legacy `checkAndUpdateLastValidTimestamp`:
 * a candidate is valid only when both the internal and external baselines are set, and the
 * candidate is at or after each of them within {@link TIMESTAMP_CHECK_TOLERANCE_MS}. On a valid
 * check the internal timestamp advances to the candidate — it is only ever allowed to move
 * forward. `lastCheck` is always recorded, valid or not, carrying the *pre-state* baselines so
 * the reporting effects (`reportCheck$`, `appLock$`) can log and lock without recomputing
 * anything.
 *
 * The empty string ("not set") deliberately is not epoch 0: `Date.parse('')` is `NaN`, so an
 * unset baseline fails every comparison against it rather than looking like 1970.
 *
 * Shared by both {@link timestampCheckRequested} and `initialTimestampCheckRequested` — see the
 * actions' doc comments for why they are two actions rather than one.
 *
 * @param state the date-manipulation-check slice
 * @param props the action payload
 * @param props.timestamp the candidate timestamp to check, as an ISO string
 * @returns the slice with the check recorded, and the internal timestamp advanced when valid
 */
export function dateManipulationCheckTimestampReducer(
    state: Readonly<DateManipulationCheckState>,
    props: { timestamp: string },
): Readonly<DateManipulationCheckState> {
    const candidateMs = Date.parse(props.timestamp);
    const internalSet = state.lastValidInternalTimestamp !== '';
    const externalSet = state.lastValidExternalTimestamp !== '';

    const isInternallyValid =
        candidateMs >= Date.parse(state.lastValidInternalTimestamp) - TIMESTAMP_CHECK_TOLERANCE_MS;

    let isExternallyValid = false;
    if (externalSet) {
        isExternallyValid = candidateMs >= Date.parse(state.lastValidExternalTimestamp) - TIMESTAMP_CHECK_TOLERANCE_MS;
    }

    const valid = internalSet && isInternallyValid && externalSet && isExternallyValid;

    return {
        ...state,
        lastValidInternalTimestamp: valid ? props.timestamp : state.lastValidInternalTimestamp,
        lastCheck: {
            checkedTimestamp: props.timestamp,
            previousInternalTimestamp: state.lastValidInternalTimestamp,
            previousExternalTimestamp: state.lastValidExternalTimestamp,
            valid,
        },
    };
}

/**
 * Marks the slice as loaded. Dispatched by `completeInit$` regardless of whether the init check
 * passed — see that effect's doc comment for why the swallowed init-time error is preserved.
 *
 * @param state the date-manipulation-check slice
 */
export function dateManipulationCheckSetLoadedReducer(
    state: Readonly<DateManipulationCheckState>,
): Readonly<DateManipulationCheckState> {
    return { ...state, loadStatus: LoadStatus.Loaded };
}

/**
 * Marks the slice as failed, leaving the timestamps already held in place.
 *
 * @param state the date-manipulation-check slice
 */
export function dateManipulationCheckSetErrorReducer(
    state: Readonly<DateManipulationCheckState>,
): Readonly<DateManipulationCheckState> {
    return { ...state, loadStatus: LoadStatus.Error };
}

const createDateManipulationCheckReducer = createReducer(
    initialDateManipulationCheckState,
    on(initDateManipulationCheck, dateManipulationCheckInitReducer),
    on(dateManipulationCheckTimestampsRestoredFromStorage, dateManipulationCheckRestoreTimestampsReducer),
    on(externalTimestampReceived, dateManipulationCheckSetExternalTimestampReducer),
    on(timestampCheckRequested, dateManipulationCheckTimestampReducer),
    on(initialTimestampCheckRequested, dateManipulationCheckTimestampReducer),
    on(dateManipulationCheckLoaded, dateManipulationCheckSetLoadedReducer),
    on(dateManipulationCheckFailed, dateManipulationCheckSetErrorReducer),
);

export function dateManipulationCheckReducer(
    state: Readonly<DateManipulationCheckState> | undefined,
    action: Action,
): Readonly<DateManipulationCheckState> {
    return createDateManipulationCheckReducer(state, action);
}
